import React, { useState, useEffect } from 'react';
import { 
  Container, Typography, Box, Paper, Button, 
  Table, TableBody, TableCell, TableContainer, TableHead, 
  TableRow, Chip, IconButton, Dialog, DialogActions, 
  DialogContent, DialogTitle, TextField, Grid, FormControlLabel,
  Switch, MenuItem, Select, FormControl, InputLabel, Alert,
  CircularProgress, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { 
  getAIPrompts, 
  getAIModels,
  createAIPrompt, 
  updateAIPrompt, 
  deleteAIPrompt 
} from '../services/aiService';

const AIPrompts = () => {
  const [prompts, setPrompts] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('add'); // 'add', 'edit', or 'clone'
  const [currentPrompt, setCurrentPrompt] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    prompt: '',
    variables: {},
    aiModelId: ''
  });
  const [alert, setAlert] = useState({ show: false, severity: 'info', message: '' });
  const [confirmDelete, setConfirmDelete] = useState(false);
  
  // Load prompts and models on component mount
  useEffect(() => {
    fetchPrompts();
    fetchModels();
  }, []);
  
  const fetchPrompts = async () => {
    setLoading(true);
    try {
      const response = await getAIPrompts();
      setPrompts(response.data || []);
    } catch (error) {
      console.error('Error fetching AI prompts:', error);
      showAlert('Failed to load AI prompts', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchModels = async () => {
    try {
      const response = await getAIModels();
      setModels(response.data || []);
    } catch (error) {
      console.error('Error fetching AI models:', error);
    }
  };
  
  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  const handleVariablesChange = (e) => {
    try {
      // Try to parse the JSON input
      const jsonValue = e.target.value.trim() === '' ? {} : JSON.parse(e.target.value);
      setFormData({
        ...formData,
        variables: jsonValue
      });
    } catch (error) {
      // If not valid JSON, store as string to allow user to continue editing
      setFormData({
        ...formData,
        variables: e.target.value
      });
    }
  };
  
  const openAddForm = () => {
    setFormData({
      name: '',
      description: '',
      prompt: '',
      variables: {},
      aiModelId: models.length > 0 ? models.find(m => m.is_default)?.id || models[0].id : ''
    });
    setFormMode('add');
    setFormOpen(true);
  };
  
  const openEditForm = (prompt) => {
    setCurrentPrompt(prompt);
    setFormData({
      name: prompt.name,
      description: prompt.description || '',
      prompt: prompt.prompt,
      variables: prompt.variables || {},
      aiModelId: prompt.ai_model_id || '' // Backend uses snake_case
    });
    setFormMode('edit');
    setFormOpen(true);
  };
  
  const openCloneForm = (prompt) => {
    setCurrentPrompt(prompt);
    setFormData({
      name: `Copy of ${prompt.name}`,
      description: prompt.description || '',
      prompt: prompt.prompt,
      variables: prompt.variables || {},
      aiModelId: prompt.ai_model_id || ''
    });
    setFormMode('clone');
    setFormOpen(true);
  };
  
  const openDeleteConfirm = (prompt) => {
    setCurrentPrompt(prompt);
    setConfirmDelete(true);
  };
  
  const closeForm = () => {
    setFormOpen(false);
    setCurrentPrompt(null);
  };
  
  const closeConfirmDelete = () => {
    setConfirmDelete(false);
    setCurrentPrompt(null);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Validate form
      if (!formData.name || !formData.prompt) {
        showAlert('Please fill all required fields', 'error');
        return;
      }
      
      // Ensure variables is a valid JSON object
      let validForm = true;
      try {
        if (typeof formData.variables === 'string') {
          formData.variables = JSON.parse(formData.variables);
        }
      } catch (error) {
        showAlert('Invalid JSON in variables', 'error');
        validForm = false;
      }
      
      if (!validForm) return;
      
      if (formMode === 'add' || formMode === 'clone') {
        // Create new prompt
        await createAIPrompt(formData);
        showAlert('AI prompt created successfully', 'success');
      } else {
        // Update existing prompt
        await updateAIPrompt(currentPrompt.id, formData);
        showAlert('AI prompt updated successfully', 'success');
      }
      
      // Close form and refresh list
      closeForm();
      fetchPrompts();
    } catch (error) {
      console.error('Error saving AI prompt:', error);
      showAlert(error.response?.data?.error || 'Failed to save AI prompt', 'error');
    }
  };
  
  const handleDelete = async () => {
    try {
      await deleteAIPrompt(currentPrompt.id);
      showAlert('AI prompt deleted successfully', 'success');
      fetchPrompts();
      closeConfirmDelete();
    } catch (error) {
      console.error('Error deleting AI prompt:', error);
      showAlert(error.response?.data?.error || 'Failed to delete AI prompt', 'error');
    }
  };
  
  const showAlert = (message, severity = 'info') => {
    setAlert({ show: true, severity, message });
    
    // Auto-hide the alert after 5 seconds
    setTimeout(() => {
      setAlert({ ...alert, show: false });
    }, 5000);
  };
  
  const getModelNameById = (id) => {
    const model = models.find(m => m.id === id);
    return model ? model.name : 'Unknown Model';
  };
  
  return (
    <Container maxWidth="xl">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          AI Prompts
        </Typography>
        <Typography variant="body1" color="text.secondary" gutterBottom>
          Manage AI prompts for call analysis per campaign
        </Typography>
        
        {alert.show && (
          <Alert severity={alert.severity} sx={{ my: 2 }}>
            {alert.message}
          </Alert>
        )}
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={openAddForm}
          >
            Add Prompt
          </Button>
        </Box>
        
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Model</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : prompts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      No AI prompts found
                    </TableCell>
                  </TableRow>
                ) : (
                  prompts.map((prompt) => (
                    <TableRow key={prompt.id}>
                      <TableCell>{prompt.name}</TableCell>
                      <TableCell>{prompt.ai_model_id ? getModelNameById(prompt.ai_model_id) : 'Not Specified'}</TableCell>
                      <TableCell>
                        <IconButton 
                          size="small" 
                          onClick={() => openEditForm(prompt)}
                          title="Edit"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => openCloneForm(prompt)}
                          title="Clone"
                        >
                          <FileCopyIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => openDeleteConfirm(prompt)}
                          title="Delete"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        
        {/* Display prompt details in expandable accordions */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom>
            Prompt Details
          </Typography>
          
          {prompts.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No prompts available
            </Typography>
          ) : (
            prompts.map((prompt) => (
              <Accordion key={prompt.id} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>
                    {prompt.name}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {prompt.description && (
                    <>
                      <Typography variant="subtitle2" gutterBottom>
                        Description:
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        {prompt.description}
                      </Typography>
                    </>
                  )}
                  
                  <Typography variant="subtitle2" gutterBottom>
                    Prompt:
                  </Typography>
                  <Paper 
                    variant="outlined" 
                    sx={{ p: 2, mb: 2, backgroundColor: '#f5f5f5', whiteSpace: 'pre-wrap' }}
                  >
                    {prompt.prompt}
                  </Paper>
                  
                  {prompt.variables && Object.keys(prompt.variables).length > 0 && (
                    <>
                      <Typography variant="subtitle2" gutterBottom>
                        Variables:
                      </Typography>
                      <Paper 
                        variant="outlined" 
                        sx={{ p: 2, backgroundColor: '#f5f5f5' }}
                      >
                        <pre>{JSON.stringify(prompt.variables, null, 2)}</pre>
                      </Paper>
                    </>
                  )}
                </AccordionDetails>
              </Accordion>
            ))
          )}
        </Box>
      </Box>
      
      {/* Add/Edit Form Dialog */}
      <Dialog open={formOpen} onClose={closeForm} maxWidth="md" fullWidth>
        <DialogTitle>
          {formMode === 'add' ? 'Add AI Prompt' : 
           formMode === 'clone' ? 'Clone AI Prompt' : 
           'Edit AI Prompt'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  required
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  margin="normal"
                  helperText="Brief description of the prompt"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Prompt"
                  name="prompt"
                  value={formData.prompt}
                  onChange={handleFormChange}
                  multiline
                  rows={8}
                  required
                  margin="normal"
                  helperText="The actual prompt template sent to the AI model"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Variables (JSON)"
                  name="variables"
                  value={typeof formData.variables === 'object' ? 
                    JSON.stringify(formData.variables, null, 2) : 
                    formData.variables}
                  onChange={handleVariablesChange}
                  multiline
                  rows={4}
                  margin="normal"
                  helperText="Variables for the prompt"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="ai-model-label">AI Model</InputLabel>
                  <Select
                    labelId="ai-model-label"
                    name="aiModelId"
                    value={formData.aiModelId}
                    onChange={handleFormChange}
                  >
                    <MenuItem value="">
                      <em>Select a model</em>
                    </MenuItem>
                    {models.map((model) => (
                      <MenuItem key={model.id} value={model.id}>
                        {model.name} ({model.provider})
                        {model.is_default && ' - Default'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={handleFormChange}
                      name="isActive"
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeForm}>Cancel</Button>
            <Button 
              type="submit" 
              variant="contained"
            >
              {formMode === 'add' ? 'Add' : 
               formMode === 'clone' ? 'Create Copy' : 
               'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={confirmDelete} onClose={closeConfirmDelete}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete the AI prompt "{currentPrompt?.name}"? 
          This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDelete}>Cancel</Button>
          <Button 
            onClick={handleDelete} 
            variant="contained" 
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AIPrompts; 